import { useCallback, useEffect, useRef, useState } from 'react';
import { bandService } from '../services/bandService';

const BAND_AUDIENCE_PUBLIC = 'public';
const BAND_AUDIENCE_VIEWER = 'viewer';

const loadBandsForAudience = (userId, audience, options) => {
  if (audience === BAND_AUDIENCE_PUBLIC) {
    return bandService.listPublicUserBands(userId, options);
  }
  if (audience === BAND_AUDIENCE_VIEWER) {
    return bandService.listUserBands(userId, options);
  }
  return bandService.listMyBands(options);
};

export function useUserBands(userId, { enabled = true, audience = 'self' } = {}) {
  const [bands, setBands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const resetState = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setBands([]);
    setError(null);
    setIsLoading(false);
    setHasLoadedSuccessfully(false);
  }, []);

  const fetchBands = useCallback(async () => {
    if (!enabled || !userId) {
      resetState();
      return [];
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const data = await loadBandsForAudience(userId, audience, { signal: controller.signal });
      if (mountedRef.current) {
        setBands(Array.isArray(data) ? data : []);
        setHasLoadedSuccessfully(true);
      }
      return data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        return [];
      }
      if (mountedRef.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [audience, enabled, resetState, userId]);

  useEffect(() => {
    fetchBands().catch(() => null);
  }, [fetchBands]);

  return {
    bands,
    isLoading,
    error,
    hasLoadedSuccessfully,
    refresh: fetchBands,
  };
}

export default useUserBands;
